// src/hooks/useMultiplayer.ts
import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";

interface UseMultiplayerOptions {
  serverUrl: string;
  userId?: string; // UUID string
  username?: string;
  enabled?: boolean; // Allow disabling the hook
}

interface GameRoom {
  roomId: string;
  color: "white" | "black";
  opponent?: string;
  opponentUserId?: string;
  boardSize?: number;
  variantId?: string;
  status?: "waiting" | "ready" | "in_progress"; // Track room status
}

interface OpponentMove {
  move: any;
  gameState: any;
}

interface ChatMessage {
  sender: string;
  message: string;
  timestamp?: string;
}

export function useMultiplayer({
  serverUrl,
  userId,
  username,
  enabled = true,
}: UseMultiplayerOptions) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [currentRoom, setCurrentRoom] = useState<GameRoom | null>(null);
  const [opponentConnected, setOpponentConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  
  // ⭐ NEW: Track received game state for guests joining
  const [receivedGameState, setReceivedGameState] = useState<any>(null);
  
  // Track created games waiting for opponent
  const [createdRoomWaitingForOpponent, setCreatedRoomWaitingForOpponent] = useState<string | null>(null);
  const [opponentJoinedRoom, setOpponentJoinedRoom] = useState<string | null>(null);
  const [opponentUsername, setOpponentUsername] = useState<string | null>(null);

  // Game limit tracking
  const [gameLimitReached, setGameLimitReached] = useState<{
    reached: boolean;
    message: string;
    currentCount: number;
    limit: number;
    membershipLevel: string;
  } | null>(null);

  // Refs for stable values across re-renders
  const serverUrlRef = useRef(serverUrl);
  const userIdRef = useRef(userId);
  const usernameRef = useRef(username);
  const enabledRef = useRef(enabled);

  // Refs for callbacks
  const onOpponentMoveRef = useRef<((move: OpponentMove) => void) | null>(null);
  const onGameStartRef = useRef<((data: any) => void) | null>(null);
  const onGameFinishedRef = useRef<((data: any) => void) | null>(null);
  const onOpponentDisconnectedRef = useRef<(() => void) | null>(null);
  const onOpponentJoinedRoomRef = useRef<((data: { opponent: string; roomId: string }) => void) | null>(null);

  // Update refs when values change (doesn't trigger socket reconnection)
  useEffect(() => {
    serverUrlRef.current = serverUrl;
    userIdRef.current = userId;
    usernameRef.current = username;
    enabledRef.current = enabled;
  }, [serverUrl, userId, username, enabled]);

  // Initialize socket connection ONCE (stable connection)
  useEffect(() => {
    if (!enabled) {
      console.log("🔌 Multiplayer disabled - enabled flag is false");
      return;
    }

    if (!userId || !username) {
      console.warn("⚠️ Multiplayer enabled but missing credentials:", {
        userId: userId ? "present" : "MISSING",
        username: username || "MISSING",
      });
      return;
    }

    console.log("🔌 Initializing Socket.IO connection to", serverUrl, "with:", {
      userId,
      username,
    });

    // Initialize socket connection with authentication
    const newSocket = io(serverUrl, {
      auth: {
        userId,
        username,
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    newSocket.on("connect", () => {
      console.log("✅ Connected to multiplayer server:", newSocket.id);
      setConnected(true);
      setError(null);
    });

    newSocket.on("connect_error", (err) => {
      console.error("❌ Connection error:", err.message);
      setError(`Connection failed: ${err.message}`);
      setConnected(false);
    });

    newSocket.on("disconnect", (reason) => {
      console.log("🔌 Disconnected from server:", reason);
      setConnected(false);

      if (reason === "io server disconnect") {
        // Server disconnected us, try to reconnect
        newSocket.connect();
      }
    });

    newSocket.on("error", (data: { message: string }) => {
      console.error("❌ Socket error:", data.message);
      setError(data.message);
    });

    // Handle game limit reached
    newSocket.on("game_limit_reached", (data: {
      message: string;
      currentCount: number;
      limit: number;
      membershipLevel: string;
    }) => {
      console.log("🚫 Game limit reached:", data);
      setGameLimitReached({
        reached: true,
        message: data.message,
        currentCount: data.currentCount,
        limit: data.limit,
        membershipLevel: data.membershipLevel
      });
      setError(data.message);
    });

    newSocket.on(
      "room_created",
      (data: { roomId: string; color: "white" | "black" }) => {
        console.log("🎮 Room created:", data.roomId);
        setCurrentRoom({
          roomId: data.roomId,
          color: data.color,
          status: "waiting",
        });
        setCreatedRoomWaitingForOpponent(data.roomId);
        setError(null);
      }
    );

    // ⭐ UPDATED: Enhanced room_joined handler to capture gameState
    newSocket.on(
      "room_joined",
      (data: {
        roomId: string;
        color: "white" | "black";
        gameState: any;
        opponent: string;
        opponentUserId?: string;
      }) => {
        console.log("🎮 ========== GUEST JOINED ROOM ==========");
        console.log("   Room ID:", data.roomId);
        console.log("   Color:", data.color);
        console.log("   Opponent:", data.opponent);
        console.log("   Opponent User ID:", data.opponentUserId);
        console.log("📦 Received gameState:", data.gameState);

        // ⭐ CRITICAL: Log the board size
        if (data.gameState?.boardSize) {
          console.log(`📐 Board size from server: ${data.gameState.boardSize}×${data.gameState.boardSize}`);
        } else {
          console.error("❌ WARNING: No boardSize in gameState!");
        }

        setCurrentRoom({
          roomId: data.roomId,
          color: data.color,
          opponent: data.opponent,
          opponentUserId: data.opponentUserId,
          status: "ready",
          boardSize: data.gameState?.boardSize, // ⭐ Store board size
          variantId: data.gameState?.preset,     // ⭐ Store variant
        });
        
        // ⭐ NEW: Store the received gameState for guest initialization
        setReceivedGameState(data.gameState);
        console.log("✅ receivedGameState stored for guest board initialization");

        // Set opponent username for display
        setOpponentUsername(data.opponent || null);

        setOpponentConnected(true);
        setError(null);
        console.log("✅ ========== GUEST JOIN COMPLETE ==========");
      }
    );

    // Enhanced opponent_joined event handler
    newSocket.on("opponent_joined", (data: {
      opponent: string;
      roomId: string;
      odisplayUserId?: string;
      username?: string;
    }) => {
      console.log("👥 Opponent joined room:", data.roomId, "- Opponent:", data.opponent || data.username);
      console.log("   Opponent User ID:", data.odisplayUserId);

      // If this is the room we created, track it
      if (data.roomId === createdRoomWaitingForOpponent) {
        setOpponentJoinedRoom(data.roomId);
        setOpponentUsername(data.opponent || data.username || null);
        console.log("✅ Opponent joined YOUR room! Show 'Join Game' button");
      }

      setCurrentRoom((prev) =>
        prev ? {
          ...prev,
          opponent: data.opponent || data.username,
          opponentUserId: data.odisplayUserId,
          status: "ready"
        } : null
      );
      setOpponentConnected(true);

      // Call callback if set
      if (onOpponentJoinedRoomRef.current) {
        onOpponentJoinedRoomRef.current(data);
      }
    });

    newSocket.on("game_start", (data: any) => {
      console.log("🎮 Game started");
      if (currentRoom) {
        setCurrentRoom({ ...currentRoom, status: "in_progress" });
      }
      if (onGameStartRef.current) {
        onGameStartRef.current(data);
      }
    });

    newSocket.on("opponent_move", (data: OpponentMove) => {
      console.log("♟️ Opponent made a move");
      if (onOpponentMoveRef.current) {
        onOpponentMoveRef.current(data);
      }
    });

    newSocket.on("game_finished", (data: any) => {
      console.log("🏁 Game finished:", data);
      if (onGameFinishedRef.current) {
        onGameFinishedRef.current(data);
      }
    });

    newSocket.on("opponent_disconnected", () => {
      console.log("🔌 Opponent disconnected");
      setOpponentConnected(false);
      if (onOpponentDisconnectedRef.current) {
        onOpponentDisconnectedRef.current();
      }
    });

    newSocket.on(
      "chat_message",
      (data: { message: string; sender: string; timestamp?: string }) => {
        console.log("💬 Chat message received from", data.sender);
        console.log("   Message:", data.message);
        setChatMessages((prev) => [...prev, data]);
      }
    );

    setSocket(newSocket);

    return () => {
      console.log("🔌 Cleaning up socket connection");
      newSocket.close();
    };
  }, []); // Empty dependency array - only initialize once!

  // Create a new game room
  const createRoom = (
    variantId: string | undefined,
    boardSize: number,
    initialGameState: any,
    gameName?: string
  ) => {
    console.log("🎮 createRoom called - checking connection status...", {
      socketExists: !!socket,
      connected,
      userId: userIdRef.current,
      username: usernameRef.current,
      gameName,
    });

    if (!socket) {
      const errMsg = "Cannot create room: socket not connected";
      console.error("❌", errMsg);
      setError(errMsg);
      return;
    }

    if (!connected) {
      const errMsg = "Cannot create room: not connected to server";
      console.error("❌", errMsg);
      setError(errMsg);
      return;
    }

    console.log("🎮 Creating room with:", {
      variantId,
      boardSize,
      gameName,
      userId: userIdRef.current,
      username: usernameRef.current,
    });

    socket.emit("create_room", {
      userId: userIdRef.current,
      username: usernameRef.current,
      variantId,
      boardSize,
      initialGameState,
      gameName,
    });
  };

  // Join an existing game room
  const joinRoom = (roomId: string) => {
    if (!socket) {
      console.error("❌ Cannot join room: socket not connected");
      setError("Not connected to server");
      return;
    }

    if (!connected) {
      console.error("❌ Cannot join room: not connected");
      setError("Not connected to server");
      return;
    }

    console.log("🎮 Joining room:", roomId);
    
    // ⭐ Clear previous gameState before joining new room
    setReceivedGameState(null);
    
    socket.emit("join_room", {
      roomId,
      userId: userIdRef.current,
      username: usernameRef.current,
    });
    
    // Clear the "waiting for opponent" state since we're joining
    if (createdRoomWaitingForOpponent === roomId) {
      setCreatedRoomWaitingForOpponent(null);
      setOpponentJoinedRoom(null);
    }
  };

  // Make a move
  const makeMove = (move: any, newGameState: any) => {
    if (!socket || !currentRoom) {
      console.error("❌ Cannot make move: not in a room");
      return;
    }

    if (!connected) {
      console.error("❌ Cannot make move: not connected");
      return;
    }

    console.log("♟️ Sending move to server");
    socket.emit("make_move", {
      roomId: currentRoom.roomId,
      move,
      newGameState,
    });
  };

  // End the game
  const endGame = (winner: "white" | "black" | "draw", reason: string) => {
    if (!socket || !currentRoom) {
      console.error("❌ Cannot end game: not in a room");
      return;
    }

    console.log("🏁 Ending game:", winner, reason);
    socket.emit("game_over", {
      roomId: currentRoom.roomId,
      winner,
      reason,
    });
  };

  // Send chat message
  const sendChatMessage = (message: string) => {
    if (!socket || !currentRoom) {
      console.error("❌ Cannot send message: not in a room");
      console.error("   socket:", !!socket);
      console.error("   currentRoom:", currentRoom);
      return;
    }

    if (!connected) {
      console.error("❌ Cannot send message: not connected");
      return;
    }

    console.log("💬 Sending chat message to room:", currentRoom.roomId);
    console.log("   Message:", message);
    socket.emit("chat_message", {
      roomId: currentRoom.roomId,
      message,
    });

    // Add own message to chat immediately (optimistic update)
    setChatMessages((prev) => [...prev, { sender: "You", message }]);
  };

  // Leave current room
  const leaveRoom = () => {
    if (socket && currentRoom) {
      console.log("🚪 Leaving room:", currentRoom.roomId);
      socket.emit("leave_room", { roomId: currentRoom.roomId });
    }
    setCurrentRoom(null);
    setOpponentConnected(false);
    setChatMessages([]);
    setError(null);
    setCreatedRoomWaitingForOpponent(null);
    setOpponentJoinedRoom(null);
    setOpponentUsername(null);
    setReceivedGameState(null); // ⭐ Clear received game state
  };

  // Delete a room (only host can delete)
  const deleteRoom = (roomId: string) => {
    if (!socket) {
      console.error("❌ Cannot delete room: socket not connected");
      setError("Not connected to server");
      return;
    }

    if (!connected) {
      console.error("❌ Cannot delete room: not connected");
      setError("Not connected to server");
      return;
    }

    console.log("🗑️ Deleting room:", roomId);
    socket.emit("delete_room", {
      roomId,
      userId: userIdRef.current,
    });
    
    // Clear tracking if deleting our created room
    if (createdRoomWaitingForOpponent === roomId) {
      setCreatedRoomWaitingForOpponent(null);
      setOpponentJoinedRoom(null);
      setOpponentUsername(null);
    }
  };

  // Callback setters
  const setOnOpponentMove = (callback: (move: OpponentMove) => void) => {
    onOpponentMoveRef.current = callback;
  };

  const setOnGameStart = (callback: (data: any) => void) => {
    onGameStartRef.current = callback;
  };

  const setOnGameFinished = (callback: (data: any) => void) => {
    onGameFinishedRef.current = callback;
  };

  const setOnOpponentDisconnected = (callback: () => void) => {
    onOpponentDisconnectedRef.current = callback;
  };

  const setOnOpponentJoinedRoom = (callback: (data: { opponent: string; roomId: string }) => void) => {
    onOpponentJoinedRoomRef.current = callback;
  };

  return {
    // Connection state
    connected,
    currentRoom,
    opponentConnected,
    error,
    chatMessages,

    // Invitation tracking
    createdRoomWaitingForOpponent,
    opponentJoinedRoom,
    opponentUsername,

    // ⭐ NEW: Expose received game state for guest initialization
    receivedGameState,

    // Game limit status
    gameLimitReached,

    // Actions
    createRoom,
    joinRoom,
    makeMove,
    endGame,
    sendChatMessage,
    leaveRoom,
    deleteRoom,

    // Event handlers
    setOnOpponentMove,
    setOnGameStart,
    setOnGameFinished,
    setOnOpponentDisconnected,
    setOnOpponentJoinedRoom,
  };
}
